'use-client';
import * as React from 'react';
import cn from 'clsx';
import { getDataAttributes } from '../../../../../utils/dataUtils';
import type { MenuItemProps } from '../../../../../types/vendored/editor-elements-definitions';
import type { IMenuItemSDKAction } from '../../../../../common/sdk/props-factories/menuItemsPropsSDKFactory';
import type { FC } from 'react';
import type { SubmenuPreviewMapperProps, SubmenuProps } from './types';
import { useMenuItemContext } from '../../contexts/MenuItemContext';
import { isCurrentItem } from '../../menuItems';
import styles from './styles/Submenu.module.scss';
import menuItemStyles from '../MenuContent/MenuItem/style/MenuItem.module.scss';
import Link from '../../../../../common/Link/Link';
import { TestIds, DataHooks, INNER_FOCUS_RING_CLASSNAME } from './constants';
import {
  getItemDepthSelector,
  subItemDepthLevel,
  subSubItemDepthLevel,
} from '../../utils';
import { DesignStates, MenuParts, selectors } from '../../constants';
import { createSDKAction } from '../MenuContent/MenuItem/utils';

type ItemListProps = SubmenuPreviewMapperProps & {
  items: Array<MenuItemProps>;
  currentItem?: MenuItemProps;
  isRootLevel?: boolean;
  onEscKeyDown?: () => void;
  onItemClick?: IMenuItemSDKAction;
  onItemMouseIn?: IMenuItemSDKAction;
  onItemMouseOut?: IMenuItemSDKAction;
  onItemDblClick?: IMenuItemSDKAction;
  dropdownItemClassName?: string;
  dropdownSubItemClassName?: string;
};

const ItemList: FC<ItemListProps> = ({
  items,
  currentItem,
  isRootLevel = true,
  partToPreviewStateMap,
  onEscKeyDown,
  onItemClick,
  onItemMouseIn,
  onItemMouseOut,
  onItemDblClick,
  dropdownItemClassName,
  dropdownSubItemClassName,
}) => {
  const subMenuContainerClasses = cn(
    isRootLevel ? styles.list : styles.subList,
    menuItemStyles.dropdownMenu,
  );

  const submenuItemLinkClasses = (isCurrent: boolean) =>
    cn(
      isRootLevel
        ? [
            styles.item,
            selectors.dropdownItem,
            dropdownItemClassName,
            isCurrent && DesignStates.dropdownItem.selected.className,
          ]
        : [
            styles.subItem,
            selectors.dropdownSubItem,
            dropdownSubItemClassName,
            isCurrent && DesignStates.dropdownSubItem.selected.className,
          ],
      INNER_FOCUS_RING_CLASSNAME,
    );

  const submenuItemLabelClasses = cn(
    isRootLevel ? styles.itemLabel : styles.subItemLabel,
  );

  return (
    <ul
      className={subMenuContainerClasses}
      style={
        {
          '--items-number': items.length,
        } as React.CSSProperties
      }
      onKeyDown={e => {
        if (e.key === 'Escape') {
          if (onEscKeyDown) {
            onEscKeyDown();

            return;
          }

          const target = e.target as HTMLElement;
          const parentListItem = target.closest('li') as HTMLElement;

          const parentNavigateTo = parentListItem.closest(
            getItemDepthSelector(0),
          );

          (parentNavigateTo?.firstChild as HTMLElement)?.focus();
        }
      }}
    >
      {items.map((item, i) => {
        const { items: subItems, link, label } = item;
        const isCurrent = isCurrentItem(item, currentItem);

        return (
          <li
            className={cn({ [styles.listItem]: !isRootLevel })}
            key={i}
            data-item-depth={
              isRootLevel ? subItemDepthLevel : subSubItemDepthLevel
            }
          >
            <Link
              {...link}
              className={submenuItemLinkClasses(isCurrent)}
              dataPart={
                isRootLevel ? MenuParts.DropdownItem : MenuParts.DropdownSubItem
              }
              dataPreview={
                partToPreviewStateMap?.[isRootLevel ? 'item' : 'sub-item']
              }
              onFocus={e => {
                e.stopPropagation();
              }}
              onClick={createSDKAction(item, isCurrent, onItemClick)}
              onMouseEnter={createSDKAction(item, isCurrent, onItemMouseIn)}
              onMouseLeave={createSDKAction(item, isCurrent, onItemMouseOut)}
              onDoubleClick={createSDKAction(item, isCurrent, onItemDblClick)}
              {...(isCurrent && {
                'aria-current': 'page',
              })}
            >
              <span
                className={submenuItemLabelClasses}
                data-selected={isCurrent ? 'true' : undefined}
                data-part={
                  isRootLevel
                    ? MenuParts.DropdownItemLabel
                    : MenuParts.DropdownSubItemLabel
                }
                data-testid={
                  isRootLevel ? TestIds.itemLabel : TestIds.subItemLabel
                }
              >
                {label}
              </span>
            </Link>

            {!!subItems?.length && (
              <ItemList
                partToPreviewStateMap={partToPreviewStateMap}
                items={subItems}
                isRootLevel={false}
                currentItem={currentItem}
                onItemClick={onItemClick}
                onItemMouseIn={onItemMouseIn}
                onItemMouseOut={onItemMouseOut}
                onItemDblClick={onItemDblClick}
                dropdownSubItemClassName={dropdownSubItemClassName}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const Submenu: React.FC<SubmenuProps> = props => {
  const {
    id,
    partToPreviewStateMap,
    className,
    onItemClick,
    onItemMouseIn,
    onItemMouseOut,
    onItemDblClick,
    dropdownItemClassName,
    dropdownSubItemClassName,
  } = props;
  const { item, currentItem, onEscKeyDown } = useMenuItemContext();

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={cn(className, styles.root)}
      data-hook={DataHooks.submenu}
    >
      <ItemList
        partToPreviewStateMap={partToPreviewStateMap}
        items={item.items || []}
        currentItem={currentItem}
        onEscKeyDown={onEscKeyDown}
        onItemClick={onItemClick}
        onItemMouseIn={onItemMouseIn}
        onItemMouseOut={onItemMouseOut}
        onItemDblClick={onItemDblClick}
        dropdownItemClassName={dropdownItemClassName}
        dropdownSubItemClassName={dropdownSubItemClassName}
      />
    </div>
  );
};

export default Submenu;
